import { Collection } from "../ports.js";
/** Ids of the single documents in `Collection.mentorSettings`. */
const SettingsDoc = { newcomer: "newcomer", choices: "choices" };
/** At most this many early answers are kept: a stranger cannot fill a phone with answers to nothing. */
const MAX_EARLY = 20;
/** Mentor requests, introductions, the owner's mentor choices and blocks, over the Storage port. Never leaves the phone. */
export class MentorStore {
    storage;
    queue = Promise.resolve();
    constructor(storage) {
        this.storage = storage;
    }
    sent() {
        return this.storage.list(Collection.mentorRequests);
    }
    sentOn(channel) {
        return this.storage.get(Collection.mentorRequests, channel);
    }
    saveSent(r) {
        return this.storage.put(Collection.mentorRequests, r.channel, r);
    }
    received() {
        return this.storage.list(Collection.mentorAsks);
    }
    receivedOn(channel) {
        return this.storage.get(Collection.mentorAsks, channel);
    }
    saveReceived(r) {
        return this.storage.put(Collection.mentorAsks, r.channel, r);
    }
    intros() {
        return this.storage.list(Collection.mentorIntros);
    }
    intro(channel) {
        return this.storage.get(Collection.mentorIntros, channel);
    }
    /** Changes one introduction in place, one change at a time; `change` returning undefined leaves it as it was. */
    updateIntro(channel, change) {
        return this.exclusive(async () => {
            const next = change(await this.intro(channel));
            if (next)
                await this.storage.put(Collection.mentorIntros, channel, next);
            return next;
        });
    }
    /** Keeps an answer to an introduction this phone has not received yet, dropping the oldest past MAX_EARLY. */
    keepEarly(r) {
        return this.exclusive(async () => {
            const early = await this.storage.list(Collection.mentorEarly);
            for (const old of early.slice(0, Math.max(0, early.length - MAX_EARLY + 1)))
                await this.storage.delete(Collection.mentorEarly, old.channel);
            await this.storage.put(Collection.mentorEarly, r.channel, r);
        });
    }
    /** Takes the early answer kept for `channel`, if any. */
    async takeEarly(channel) {
        const early = await this.storage.get(Collection.mentorEarly, channel);
        if (early)
            await this.storage.delete(Collection.mentorEarly, channel);
        return early;
    }
    newcomer() {
        return this.storage.get(Collection.mentorSettings, SettingsDoc.newcomer);
    }
    saveNewcomer(prefs) {
        return this.storage.put(Collection.mentorSettings, SettingsDoc.newcomer, prefs);
    }
    choices() {
        return this.storage.get(Collection.mentorSettings, SettingsDoc.choices);
    }
    saveChoices(choices) {
        return this.storage.put(Collection.mentorSettings, SettingsDoc.choices, choices);
    }
    async isBlocked(user) {
        return (await this.storage.get(Collection.blocked, user)) !== undefined;
    }
    /** Everyone blocked on this phone, with when. */
    blocks() {
        return this.storage.list(Collection.blocked);
    }
    async blocked() {
        return new Set((await this.blocks()).map((b) => b.user));
    }
    block(user, at) {
        return this.storage.put(Collection.blocked, user, { user, at: at.toISOString() });
    }
    exclusive(step) {
        const run = this.queue.then(step, step);
        this.queue = run.catch(() => undefined);
        return run;
    }
}
//# sourceMappingURL=store.js.map